// ---------------------------------------------------------------------------
// sessionExpiry — turn an auth failure into ONE clean redirect to /login.
//
// When a developer's Cognito session expires mid-use, authenticated API calls
// start returning 401 (or the partner-API token mint, which is Cognito-
// authenticated, fails). Before #96 those surfaced as raw error text with no
// path back to a usable state. This module centralizes the response: redirect
// to the login page (with a flag + a return path) so the user re-authenticates.
//
// Why a hard `window.location` navigation rather than react-router?
//   - The axios interceptors that detect 401 are plain modules, not components.
//   - `AuthProvider` sits OUTSIDE `<BrowserRouter>` (App.js), so auth code can't
//     `useNavigate` either.
//   - A full navigation also force-rebootstraps the app, clearing any stale
//     in-memory auth state (cached partner tokens, react-query cache, the stale
//     `user`) — exactly what we want after a session dies.
// ---------------------------------------------------------------------------

let redirecting = false;

/**
 * Redirect to /login once, attributing it to an expired session and preserving
 * a return path. Idempotent within a page life: a burst of concurrent 401s
 * triggers a single navigation. No-op if already on /login (loop guard) or in a
 * non-browser (test/SSR) context without a location.
 */
export function notifySessionExpired() {
  if (typeof window === 'undefined' || !window.location) return;
  if (redirecting) return;
  if (window.location.pathname === '/login') return;
  redirecting = true;
  const returnTo = window.location.pathname + window.location.search;
  window.location.assign(`/login?session=expired&returnTo=${encodeURIComponent(returnTo)}`);
}

/** Test-only: reset the once-per-page dedupe guard between cases. */
export function __resetSessionExpiryForTest() {
  redirecting = false;
}
